if (!customElements.get("product-info")) {
  customElements.define(
    "product-info",
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        // Scope to the quantity_selector block's wrapper. The explicit-selection
        // PDP removed that block, but its <product-cta> widget reuses the
        // .quantity__input class — without this scope it would be mistaken for a
        // quantity selector and the quantity-rules code would run against a node
        // that no longer exists.
        this.quantityInput = this.querySelector(
          ".product-form__quantity .quantity__input"
        );
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();

        // Explicit selection: build the client-side single source of truth
        // ([data-product-variants] island) so every option pick resolves locally
        // with NO network call. Cart quantities are refreshed from the lean
        // sections/product-variant-data returned inside each cart-op response.
        if (this.hasAttribute("data-explicit-selection")) {
          this.buildVariantData();
          this.cacheUnselectedFragments();
          this.islandCartUpdateUnsubscriber = subscribe(
            PUB_SUB_EVENTS.cartUpdate,
            this.handleIslandCartUpdate.bind(this)
          );
        }

        // Explicit selection: a ?variant= landing resolves a variant server-side
        // and the inline neutralizer (main-product.liquid) flags it here. Fetch
        // the canonical unselected render to restore media that hide_variants
        // pruned from the variant render. The URL is never touched. (This is a
        // one-time landing correction, not a per-selection fetch.)
        if (
          this.hasAttribute("data-explicit-selection") &&
          this.hasAttribute("data-variant-param-reset")
        ) {
          this.resetToUnselected();
        }

        this.dispatchEvent(
          new CustomEvent("product-info:loaded", { bubbles: true })
        );
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector(".product-form__quantity");
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(
            PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this)
          );
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
        this.islandCartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html
            .querySelectorAll(".scroll-trigger")
            .forEach((element) =>
              element.classList.add("scroll-trigger--cancel")
            )
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({
        data: { event, target, selectedOptionValues },
      }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl =
          target.dataset.productUrl ||
          this.pendingRequestUrl ||
          this.dataset.url;
        const shouldSwapProduct = this.dataset.url !== productUrl;

        // Explicit selection, same product: resolve ENTIRELY client-side from the
        // [data-product-variants] island — no Section-Rendering fetch. This is the
        // fix for the OOS-leak "fighting code": the server used to auto-resolve a
        // full variant from a partial option_values fetch and its inventory tag bled
        // into an incomplete selection. A cross-product combined-listing swap
        // (target carries a different product_url) still falls through to the fetch
        // path below, since the island only holds the current product's variants.
        if (this.hasAttribute("data-explicit-selection") && !shouldSwapProduct) {
          this.resolveSelectionLocally();
          return;
        }

        this.pendingRequestUrl = productUrl;
        const shouldFetchFullPage =
          this.dataset.updateUrl === "true" && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(
            productUrl,
            selectedOptionValues,
            shouldFetchFullPage
          ),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl, selectedOptionValues),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage
            ? "product-info[id^='MainProduct']"
            : "product-info";
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector("head title").innerHTML =
              html.querySelector("head title").innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector("main"),
              html.querySelector("main"),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector("product-info"),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === "AbortError") {
              console.log("Fetch aborted by user");
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector(
          "variant-selects [data-selected-variant]"
        )?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(
        url,
        optionValues,
        shouldFetchFullPage = false
      ) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(",")}`);
        }

        return `${url}?${params.join("&")}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector("variant-selects");
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(
            this.variantSelectors,
            variantSelects,
            this.preProcessHtmlCallbacks
          );
        }
      }

      handleUpdateProductInfo(productUrl, selectedOptionValues) {
        return (html) => {
          const isExplicit = this.hasAttribute("data-explicit-selection");
          const optionCount =
            parseInt(this.variantSelectors?.dataset.optionCount, 10) || 0;
          // A "complete" selection is one value per option group. The CLIENT is
          // the source of truth for "complete": Shopify auto-resolves a full
          // variant from a PARTIAL option_values fetch (filling in the options
          // the customer hasn't chosen), so trusting the server here is what made
          // option 2 auto-select and the CTA leave "choose options". For an
          // incomplete explicit selection we treat the variant as null and never
          // swap the server's (auto-resolved) picker over the customer's.
          const isComplete =
            !isExplicit ||
            (optionCount > 0 &&
              (selectedOptionValues?.length ?? 0) >= optionCount);
          const variant =
            isExplicit && !isComplete ? null : this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          // Explicit selection keeps the customer's picker DOM authoritative —
          // greying is applied client-side (variant-availability.js), so we never
          // replace it with the server's selection.
          if (!isExplicit) this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (isExplicit) {
            this.updateProductCta(html, { variant, isComplete });
          }

          // Explicit + incomplete selection: keep product-level price (and main
          // media). No "unavailable" — the shopper is still choosing.
          if (isExplicit && !isComplete) {
            this.updateSourceFromDestination(html, "price");
            this.updateSourceFromDestination(html, "Sku", ({ classList }) =>
              classList.contains("hidden")
            );
            this.updateSourceFromDestination(
              html,
              "Inventory",
              ({ innerText }) => innerText === ""
            );
            return;
          }

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          this.updateSourceFromDestination(html, "price");
          this.updateSourceFromDestination(html, "Sku", ({ classList }) =>
            classList.contains("hidden")
          );
          this.updateSourceFromDestination(
            html,
            "Inventory",
            ({ innerText }) => innerText === ""
          );

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(
            `#Quantity-Rules-${this.dataset.section}`
          )?.classList.remove("hidden");

          this.productForm?.toggleSubmitButton(
            html
              .getElementById(`ProductSubmitButton-${this.sectionId}`)
              ?.hasAttribute("disabled") ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      // --- Client-side selection (explicit mode, no network) ------------------

      // Parse the [data-product-variants] island into entry list + id index. Each
      // entry: { id, options[], media_id, sellable, cart_quantity, price_html,
      // sku_html, inv_html }. This is the single source of truth for selection.
      buildVariantData() {
        this.variantEntries = [];
        this.variantById = new Map();
        const el = this.querySelector("[data-product-variants]");
        if (!el) return;
        try {
          const parsed = JSON.parse(el.textContent);
          if (Array.isArray(parsed)) {
            this.variantEntries = parsed;
            parsed.forEach((entry) =>
              this.variantById.set(String(entry.id), entry)
            );
          }
        } catch (error) {
          console.error(error);
        }
      }

      // The load-time price render is the product-level (unselected) price; cache it
      // so an incomplete selection can restore it without a network call. Re-cached
      // by resetToUnselected() after a ?variant= correction.
      cacheUnselectedFragments() {
        this.unselectedPriceHTML =
          this.querySelector(`#price-${this.dataset.section}`)?.innerHTML ?? "";
        // Empty unless every variant is sold out: then the server renders the
        // out-of-stock pill before a variant is chosen, and it must stay. A
        // ?variant= landing holds that variant's pill, so it is cached as empty
        // until resetToUnselected() re-caches from the unselected render.
        this.unselectedInventoryHTML = this.hasAttribute(
          "data-variant-param-reset"
        )
          ? ""
          : this.querySelector(`#Inventory-${this.dataset.section}`)
              ?.innerHTML ?? "";
      }

      // Refresh per-variant cart quantities from the lean product-variant-data
      // section returned inside a cart-op response — the server stays authoritative
      // for cart_quantity, no bespoke cart-shape parsing.
      handleIslandCartUpdate(payload) {
        const sections = payload?.cartData?.sections;
        if (!sections || !this.variantById) return;
        const html = sections["product-variant-data"];
        if (!html) return;
        try {
          const doc = new DOMParser().parseFromString(html, "text/html");
          const script = doc.querySelector("[data-product-quantities]");
          if (!script) return;
          const quantities = JSON.parse(script.textContent);
          Object.entries(quantities).forEach(([id, qty]) => {
            const entry = this.variantById.get(String(id));
            if (entry) entry.cart_quantity = qty;
          });
        } catch (error) {
          console.error(error);
        }
      }

      // Current selection as option-value STRINGS, indexed by option position - 1
      // (matches the island's `options` ordering and variant-availability.js).
      readSelectedOptionStrings() {
        const variantSelects = this.variantSelectors;
        const selected = [];
        if (!variantSelects) return selected;
        variantSelects
          .querySelectorAll("[data-option-position]")
          .forEach((group) => {
            const index = parseInt(group.dataset.optionPosition, 10) - 1;
            if (!Number.isInteger(index) || index < 0) return;
            const select = group.querySelector("select");
            if (select) {
              selected[index] = select.value || null;
              return;
            }
            const checked = group.querySelector("input:checked");
            selected[index] = checked ? checked.value : null;
          });
        return selected;
      }

      findVariantEntry(selected) {
        return this.variantEntries?.find(
          (entry) =>
            entry.options.length === selected.length &&
            entry.options.every((opt, i) => opt === selected[i])
        );
      }

      // Resolve the current picker state with zero network. Incomplete -> keep
      // product-level price, hide sku/inventory, "choose options". Complete + match
      // -> swap that variant's pre-rendered fragments + media + CTA. Complete + no
      // match -> unavailable.
      resolveSelectionLocally() {
        const optionCount =
          parseInt(this.variantSelectors?.dataset.optionCount, 10) || 0;
        const selected = this.readSelectedOptionStrings();
        const filled = selected.filter((v) => v != null && v !== "").length;
        const isComplete = optionCount > 0 && filled >= optionCount;

        if (!isComplete) {
          this.applyIncompleteSelection();
          return;
        }
        const entry = this.findVariantEntry(selected);
        if (!entry) {
          this.applyUnavailableSelection();
          return;
        }
        this.applyVariantEntry(entry);
      }

      setFragment(id, html, hide) {
        const el = this.querySelector(`#${id}-${this.dataset.section}`);
        if (!el) return;
        el.innerHTML = html || "";
        el.classList.toggle("hidden", !!hide);
        // Sku/Inventory carry a load-time `visibility-hidden` (e.g. first variant has
        // no SKU / untracked inventory); clear it when we have content to show.
        if (!hide) el.classList.remove("visibility-hidden");
      }

      applyIncompleteSelection() {
        const priceEl = this.querySelector(`#price-${this.dataset.section}`);
        if (priceEl && this.unselectedPriceHTML != null) {
          priceEl.innerHTML = this.unselectedPriceHTML;
          priceEl.classList.remove("hidden");
        }
        this.setFragment("Sku", "", true);
        const unselectedInv = this.unselectedInventoryHTML ?? "";
        this.setFragment("Inventory", unselectedInv, unselectedInv.trim() === "");

        const cta = this.querySelector("product-cta");
        if (cta) {
          const anySellable =
            (cta.getAttribute("data-any-sellable") ?? "true") !== "false";
          cta.dataset.ctaState = anySellable ? "select-options" : "soldout";
        }
        this.updateVariantInputs(null);
        this.updateURL(this.dataset.url, null);
        this.ensureVariantMedia(null);
      }

      applyUnavailableSelection() {
        this.setUnavailable();
        const cta = this.querySelector("product-cta");
        if (cta) cta.dataset.ctaState = "unavailable";
        this.updateVariantInputs(null);
      }

      applyVariantEntry(entry) {
        this.setFragment("price", entry.price_html, false);
        const skuEmpty = !entry.sku_html || entry.sku_html.trim() === "";
        this.setFragment("Sku", entry.sku_html, skuEmpty);
        const invEmpty = !entry.inv_html || entry.inv_html.trim() === "";
        this.setFragment("Inventory", entry.inv_html, invEmpty);

        const cap = entry.sellable; // null = unlimited
        const sellable = cap == null ? true : Number(cap) >= 1;
        const qty = Number(entry.cart_quantity) || 0;

        const cta = this.querySelector("product-cta");
        if (cta) {
          cta.dataset.variantId = String(entry.id);
          cta.dataset.cartQuantity = String(qty);
          if (cap == null) cta.removeAttribute("data-maximum-sellable-quantity");
          else cta.dataset.maximumSellableQuantity = String(cap);
          cta.dataset.baseState = sellable ? "add" : "soldout";
          // Reuse product-cta's own in-cart/reduce/remove resolution (single source
          // for cart-family state); it also sets data-target-quantity.
          const state =
            qty > 0 ? cta.resolveInCartState(qty) : cta.dataset.baseState;
          cta.dataset.ctaState = state;
        }

        // The add / soldout states share the SAME submit button (component-product-cta
        // .css), so its disabled state + label is the only thing distinguishing them.
        // The load-time button reflects the FIRST variant, so sync it to the SELECTED
        // one: sellable -> enabled + "add to cart", not sellable -> disabled + "sold out".
        this.productForm?.toggleSubmitButton(
          !sellable,
          window.variantStrings.soldOut
        );

        this.updateVariantInputs(entry.id);
        // A non-sellable selected variant keeps its id but must not be submittable
        // (mirrors fixed-buy-buttons; also neutralizes any dynamic-checkout button).
        if (!sellable) {
          this.querySelectorAll(
            `#product-form-${this.dataset.section} input[name="id"]`
          ).forEach((input) => {
            input.disabled = true;
          });
        }

        this.updateURL(this.dataset.url, entry.id);
        this.ensureVariantMedia(entry.media_id);
      }

      // Inject (from the <template data-variant-media>) and activate the selected
      // variant's media. hide_variants prunes variant media from the live gallery,
      // so we clone it in on demand. Passing null reverts to the product's media.
      ensureVariantMedia(mediaId) {
        const gallery = this.querySelector("product-media-gallery");
        if (!gallery) return;

        gallery
          .querySelectorAll("[data-variant-injected]")
          .forEach((li) => {
            if (li.dataset.mediaId !== mediaId) li.remove();
          });

        if (!mediaId) {
          const first = gallery.querySelector(
            ".product__media-list [data-media-id]:not([data-variant-injected])"
          );
          if (first) gallery.setActiveMedia?.(first.dataset.mediaId, false);
          return;
        }

        let li = gallery.querySelector(`[data-media-id="${mediaId}"]`);
        if (!li) {
          const source = this.querySelector(
            "template[data-variant-media]"
          )?.content.querySelector(`[data-media-id="${mediaId}"]`);
          if (source) {
            li = source.cloneNode(true);
            li.setAttribute("data-variant-injected", "");
            (
              gallery.querySelector(".product__media-list") ||
              gallery.querySelector("ul")
            )?.prepend(li);
          }
        }
        gallery.setActiveMedia?.(mediaId, true);
      }

      // Copy a subtree (price/Sku/Inventory) from the fetched document into the
      // live DOM, toggling `hidden` per `shouldHide`. Extracted from the former
      // inline closure so resetToUnselected() can reuse it.
      updateSourceFromDestination(html, id, shouldHide = () => false) {
        const source = html.getElementById(`${id}-${this.sectionId}`);
        const destination = this.querySelector(`#${id}-${this.dataset.section}`);
        if (source && destination) {
          destination.innerHTML = source.innerHTML;
          destination.classList.toggle("hidden", shouldHide(source));
        }
      }

      // Copy the freshly-computed CTA state across from the fetched <product-cta>
      // (which knows cart quantity + cap server-side). The server can't tell
      // "nothing selected" from "all options selected, no matching variant" —
      // both render select-options — so the client forces "unavailable" when it
      // knows the selection is complete but no variant resolved.
      updateProductCta(html, { variant, isComplete }) {
        const destination = this.querySelector("product-cta");
        if (!destination) return;

        const source = html.querySelector("product-cta");

        // Only adopt the server's resolved-variant attributes once the selection
        // is COMPLETE — for a partial pick the server auto-resolved a variant we
        // must ignore. data-any-sellable is product-level and stays on the
        // destination from the initial render.
        if (isComplete && source) {
          [
            "data-base-state",
            "data-variant-id",
            "data-cart-quantity",
            "data-maximum-sellable-quantity",
            "data-target-quantity",
          ].forEach((attr) => {
            const value = source.getAttribute(attr);
            if (value === null) destination.removeAttribute(attr);
            else destination.setAttribute(attr, value);
          });
        }

        let state;
        if (!isComplete) {
          // Until every option is chosen, stay on "choose options" — unless the
          // whole product is out of stock, in which case show soldout up front.
          const anySellable =
            (destination.getAttribute("data-any-sellable") ?? "true") !== "false";
          state = anySellable ? "select-options" : "soldout";
        } else if (!variant) {
          state = "unavailable";
        } else {
          state =
            source?.getAttribute("data-cta-state") ??
            destination.getAttribute("data-cta-state");
        }
        destination.setAttribute("data-cta-state", state);
      }

      // The unselected server render drops the chosen options (no full variant
      // resolved), so re-check them after the swap to keep partial progress
      // visible. Inputs/options carry data-option-value-id.
      reapplyOptionSelections(selectedOptionValues) {
        const variantSelects = this.variantSelectors;
        if (!variantSelects || !selectedOptionValues?.length) return;

        selectedOptionValues.forEach((valueId) => {
          const input = variantSelects.querySelector(
            `input[data-option-value-id="${valueId}"]`
          );
          if (input) {
            input.checked = true;
            const selectedValue = input
              .closest(".product-form__input")
              ?.querySelector("[data-selected-value]");
            if (selectedValue) selectedValue.textContent = input.value;
            return;
          }
          const option = variantSelects.querySelector(
            `option[data-option-value-id="${valueId}"]`
          );
          if (option) {
            const select = option.closest("select");
            if (select) select.value = option.value;
          }
        });
      }

      // ?variant= landing correction: fetch the canonical unselected render (no
      // params) and swap the guarded subtrees. Restores the product main media
      // that hide_variants prunes from a variant-specific render. URL untouched.
      resetToUnselected() {
        this.removeAttribute("data-variant-param-reset");
        fetch(`${this.dataset.url}?section_id=${this.sectionId}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );

            this.updateOptionValues(html);

            // Restore the product's main media client-side instead of swapping the
            // whole <product-media-gallery> via viewTransition: re-inserting the
            // gallery re-runs its connectedCallback before the child slider has
            // initialised (sliderItemsToShow undefined -> setActiveElement throws).
            // ensureVariantMedia activates the product media on the EXISTING gallery
            // (cloning it from the variant-media template if hide_variants pruned it).
            this.ensureVariantMedia(this.dataset.productMediaId || null);

            this.updateSourceFromDestination(html, "price");
            this.updateSourceFromDestination(html, "Sku", ({ classList }) =>
              classList.contains("hidden")
            );
            this.updateSourceFromDestination(
              html,
              "Inventory",
              ({ innerText }) => innerText === ""
            );

            this.updateProductCta(html, { variant: null, isComplete: false });
            this.updateVariantInputs(null);

            // The DOM now holds the product-level price again — re-cache it so a
            // later incomplete selection restores the correct unselected price.
            this.cacheUnselectedFragments();
          })
          .catch((error) => console.error(error));
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          if (!input) return;
          input.value = variantId ?? "";
          // Explicit selection: an empty id input must not be submittable.
          if (this.hasAttribute("data-explicit-selection")) {
            input.disabled = !variantId;
          }
          input.dispatchEvent(new Event("change", { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        // Explicit selection: the variant param must never influence the PDP, so
        // never write it to the URL or the share link.
        const explicit = this.hasAttribute("data-explicit-selection");
        const variantQuery = explicit || !variantId ? "" : `?variant=${variantId}`;

        this.querySelector("share-button")?.updateUrl(
          `${window.shopUrl}${url}${explicit ? "" : variantQuery}`
        );

        if (this.dataset.updateUrl === "false") return;
        window.jdHistory.replaceMerged({}, `${url}${variantQuery}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(
          true,
          window.variantStrings.unavailable
        );

        const selectors = [
          "price",
          "Inventory",
          "Sku",
          "Quantity-Rules",
        ]
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(", ");
        document
          .querySelectorAll(selectors)
          .forEach(({ classList }) => classList.add("hidden"));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector(
          "product-media-gallery ul"
        );
        const mediaGalleryDestination = html.querySelector(
          `product-media-gallery ul`
        );

        const refreshSourceData = () => {
          if (this.hasAttribute("data-zoom-on-hover")) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(
            mediaGallerySource.querySelectorAll("li[data-media-id]")
          );
          const sourceSet = new Set(
            mediaGallerySourceItems.map((item) => item.dataset.mediaId)
          );
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [
              item.dataset.mediaId,
              { item, index },
            ])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] =
            refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll("li[data-media-id]")
          );
          const destinationSet = new Set(
            mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId)
          );
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (
              !sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)
            ) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (
              !destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)
            ) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh)
            [mediaGallerySourceItems, sourceSet, sourceMap] =
              refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach(
            (destinationItem, destinationIndex) => {
              const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

              if (sourceData && sourceData.index !== destinationIndex) {
                mediaGallerySource.insertBefore(
                  sourceData.item,
                  mediaGallerySource.querySelector(
                    `li:nth-of-type(${destinationIndex + 1})`
                  )
                );

                // refresh source now that it has been modified
                [mediaGallerySourceItems, sourceSet, sourceMap] =
                  refreshSourceData();
              }
            }
          );
        }

        // set featured media as active in the media gallery
        this.querySelector(`product-media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(
          `.product-media-modal__content`
        );
        const newModalContent = html.querySelector(
          `product-modal .product-media-modal__content`
        );
        if (modalContent && newModalContent)
          modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity
            ? parseInt(this.quantityInput.dataset.cartQuantity)
            : 0,
          min: this.quantityInput.dataset.min
            ? parseInt(this.quantityInput.dataset.min)
            : 1,
          max: this.quantityInput.dataset.max
            ? parseInt(this.quantityInput.dataset.max)
            : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute("max");
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector(
          ".quantity__rules-cart .loading__spinner"
        ).classList.remove("hidden");
        return fetch(
          `${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`
        )
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() =>
            this.querySelector(
              ".quantity__rules-cart .loading__spinner"
            ).classList.add("hidden")
          );
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(
          `Quantity-Form-${sectionId}`
        );
        if (!this.quantityForm || !quantityFormUpdated) return;
        const selectors = [
          ".quantity__input",
          ".quantity__rules",
          ".quantity__label",
        ];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === ".quantity__input") {
            const attributes = [
              "data-cart-quantity",
              "data-maximum-sellable-quantity",
              "data-min",
              "data-max",
              "step",
            ];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector("variant-selects");
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          "related-products"
        );
        return document.querySelector(
          `product-recommendations[data-section-id^="${relatedProductsSectionId}"]`
        );
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          "quick_order_list"
        );
        return document.querySelector(
          `quick-order-list[data-id^="${quickOrderListSectionId}"]`
        );
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
